import type { FC, ReactNode } from 'react'
import type { MediaItem } from '@/types'

import { ArrowRight } from 'lucide-react'
import { MediaFrame } from '@/components/generals/media-frame'
import { TransitionLink } from '@/components/generals/transition-link'

interface NextPageProps {
    href: string
    index: string
    title: string
    teaser: string
    image: MediaItem
}

/** A full-bleed doorway to the next page, closing every page before the footer. */
export const NextPage: FC<NextPageProps> = ({ href, index, title, teaser, image }): ReactNode => {
    return (
        <section aria-label='Next page' className='relative z-10 bg-charcoal'>
            <TransitionLink href={href} className='group relative block h-[78svh] min-h-[440px] overflow-hidden text-bone'>
                <MediaFrame
                    image={image}
                    sizes='100vw'
                    parallax={10}
                    className='absolute inset-0 h-full w-full bg-pine'
                    imageClassName='transition-transform duration-[1800ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]'
                />

                <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/35 to-pine/20 transition-opacity duration-700 group-hover:opacity-80' />

                <span className='absolute inset-x-0 bottom-0 flex flex-col gap-6 px-5 pb-10 sm:px-8 lg:flex-row lg:items-end lg:justify-between lg:px-12 lg:pb-14'>
                    <span className='block'>
                        <span className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/75'>
                            <span>
                                Next chapter
                            </span>

                            <span aria-hidden='true' className='h-px w-8 bg-current opacity-50' />

                            <span className='tabular-nums'>
                                {index}
                            </span>
                        </span>

                        <span className='type-wide mt-5 block text-[clamp(2.4rem,7.4vw,7.5rem)] font-medium uppercase leading-[0.9] tracking-[-0.03em]'>
                            {title}
                        </span>

                        <span className='mt-5 block max-w-lg font-serif text-xl italic text-bone/85 sm:text-2xl'>
                            {teaser}
                        </span>
                    </span>

                    <span className='flex size-16 shrink-0 items-center justify-center rounded-full border border-bone/40 transition-colors duration-500 group-hover:border-bone group-hover:bg-bone group-hover:text-charcoal sm:size-20'>
                        <ArrowRight aria-hidden='true' className='size-6 transition-transform duration-500 group-hover:translate-x-1' />
                    </span>
                </span>
            </TransitionLink>
        </section>
    )
}
